import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import Checkbox from '@orn/shared-web/components/Checkbox/Checkbox';
import DateOfBirthInput from '@orn/shared-web/components/DateOfBirthInput';
import Input from '@orn/shared-web/components/Input';
import Select from '@orn/shared-web/components/Select/Select';
import { MailIcon } from '@orn/shared-web/icons';

import { RELATIONSHIP_OPTIONS } from '../../../lib/dependents/constants';
import { displayName } from '../../../lib/dependents/helpers';
import { toResidenceAddress } from '../../../lib/dependents/residenceAddress';
import { CardSection, VerifiablePhoneField } from '../common';

import { mergeFrequentPlaces, needsEmailBeforePhone, visibleFrequentPlaces } from './dependentCardModel';
import FrequentLocationsForm from './FrequentLocationsForm';
import ResidenceTypeFields from './ResidenceTypeFields';

import type { IDependentCardState, IResidenceFieldErrors, ResidenceMode } from './dependentCardModel';
import type { IFrequentLocationsState, IFrequentPlace } from './residenceForm';
import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IResidence } from '../../../hooks/queries/useDependentResidences';

/** Identity/contact errors plus the linked living-situation unit's (`IResidenceFieldErrors`). */
export interface IDependentCardErrors extends IResidenceFieldErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  /** A malformed address, a missing one once enrolled, or one the server would not take. */
  email?: string;
  /** A number the server would not take — unparseable, or verified elsewhere. */
  phone?: string;
}

export interface IDependentCardBodyProps {
  dependentId: string;
  state: IDependentCardState;
  onChange: (patch: Partial<IDependentCardState>) => void;
  /** Persist pending edits — fired on text-input blur and on select/checkbox/pick changes. */
  onCommit: () => void;
  /** Main shows the full living-situation editor; Referential shows the "lives with {partnerName}" copy. */
  mode: ResidenceMode;
  /** True for the second card of a partnered pair — renders the "Lives with {partnerName}" checkbox. */
  isPartnerCard: boolean;
  partnerName: string | null;
  /** The partnership "live together" value + setter (only meaningful on the partner card). */
  liveTogether: boolean | null;
  onLiveTogetherChange: (value: boolean) => void;
  /** Shared, household-wide frequent-locations list (mirrored across every Main card). */
  frequentLocations: IFrequentLocationsState;
  onFrequentLocationsChange: (places: IFrequentPlace[]) => void;
  /** The caregiver's own primary residence, shown read-only when "lives with you" is checked. */
  caregiverResidence: IResidence | null;
  apiKey: string;
  /** Locks every input — only while the household state is being rebuilt after a residence create/delete. */
  isDisabled: boolean;
  /**
   * Whether an account answers for this person. Their contact then lives on
   * that account: both fields stay editable and route there, and both are
   * required, since the account is reached on them.
   */
  isAccountBacked: boolean;
  /**
   * Whether an email address is saved on the row — not whether one is typed. The
   * verification the phone field offers is refused server-side without a stored
   * address, so an unflushed edit must not lift the gate.
   */
  hasEmailOnFile: boolean;
  /** Whether the address on screen differs from the saved one — an unflushed edit. */
  isEmailChanged: boolean;
  /**
   * When they last proved they hold the number, or `null` when nobody has.
   * Only meaningful for an account-backed member.
   */
  phoneNumberVerifiedAt: string | null;
  /**
   * When the outstanding attempt to prove the number started, or `null` when
   * none is outstanding. Non-null is why the button reports rather than offers:
   * the handshake runs over two other handsets, so there is nothing this card
   * can do to move it along.
   */
  phoneVerificationStartedAt: string | null;
  /**
   * Whether the typed number differs from the one on file. An edit in flight
   * withdraws the Verify button — verifying would prove the stored number, not
   * the one on screen — and puts up the warning instead. A handshake already out
   * on two handsets outranks it: the button stays, inert, reporting
   * "Verifying…", because an edit cannot un-send what is already sent.
   */
  isPhoneChanged: boolean;
  /** Open the confirmation step. Absent for a member no verification applies to. */
  onStartVerification?: () => void;
  /** True between the confirmation and the server's answer. */
  isStartingVerification: boolean;
  errors: IDependentCardErrors;
}

/**
 * What an unsaved change to an enrolled loved one's address will cost: the write
 * leaves the new address unproven until they confirm it.
 */
const EMAIL_RECONFIRM_NOTE = "They'll need to confirm this email address after this change.";

/** Why the button offers, and what the offer costs. */
const VERIFY_TOOLTIP =
  'Starts a group text with them so they can confirm this number reaches them. You take part too — nothing is sent until you confirm.';

/**
 * Why the number cannot be entered or proven yet. The rule itself, including why
 * it reads the saved address rather than the typed one, is
 * {@link needsEmailBeforePhone}.
 */
const EMAIL_FIRST_NOTE = 'Please save an email address first.';

/** Read-only display of the caregiver's address, shown when a dependent lives with the caregiver. */
const CaregiverResidenceDisplay = ({ residence }: { residence: IResidence | null }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-secondary text-sm font-medium">Address</span>
    <p className="text-tertiary text-sm">
      {residence ? (residence.formatted ?? residence.addressLine1 ?? residence.city) : 'Same as your address.'}
    </p>
  </div>
);
CaregiverResidenceDisplay.displayName = 'CaregiverResidenceDisplay';

/**
 * The editable sections for one dependent — Identity, Contact, Living
 * situation, and (Main mode) the household-shared Frequent locations. Card
 * chrome is supplied by the caller: the desktop accordion card or a
 * mobile/tablet person-tab panel. Edits persist via `onCommit` — text inputs
 * on blur, selects/checkboxes/picks on change; there is no save button. Fully
 * controlled — all state + callbacks come from the form wrapper.
 */
const DependentCardBody = ({
  dependentId,
  state,
  onChange,
  onCommit,
  mode,
  isPartnerCard,
  partnerName,
  liveTogether,
  onLiveTogetherChange,
  frequentLocations,
  onFrequentLocationsChange,
  caregiverResidence,
  apiKey,
  isDisabled,
  isAccountBacked,
  hasEmailOnFile,
  isEmailChanged,
  phoneNumberVerifiedAt,
  phoneVerificationStartedAt,
  isPhoneChanged,
  onStartVerification,
  isStartingVerification,
  errors,
}: IDependentCardBodyProps) => {
  const name = displayName({ firstName: state.firstName, preferredName: state.preferredName }) || 'Loved one';
  // Proving a loved one's number ends in an account being minted for them, and
  // `users.email` is `NOT NULL` and globally unique — so before anyone has
  // enrolled, the address has to land first. Once an account answers for them it
  // carries its own address and this stops applying.
  const needsEmailFirst = needsEmailBeforePhone({
    isAccountBacked,
    hasEmailOnFile,
    hasEmailError: Boolean(errors.email),
    isEmailChanged,
  });
  const updateResidence = (patch: Partial<IDependentCardState['residence']>) =>
    onChange({ residence: { ...state.residence, ...patch } });
  // Saved rows mirror across cards (one household list); unsaved drafts render
  // only on the card that added them, joining the mirror once a save persists them.
  const cardFrequentPlaces = visibleFrequentPlaces(frequentLocations.places, dependentId);
  const handleFrequentPlacesChange = (nextVisible: IFrequentPlace[]) =>
    onFrequentLocationsChange(mergeFrequentPlaces(frequentLocations.places, dependentId, nextVisible));

  return (
    <>
      <CardSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            value={state.firstName}
            onChange={(v) => onChange({ firstName: v })}
            onBlur={onCommit}
            size="md"
            isDisabled={isDisabled}
            isInvalid={Boolean(errors.firstName)}
            hint={errors.firstName}
          />
          <Input
            label="Last name"
            value={state.lastName}
            onChange={(v) => onChange({ lastName: v })}
            onBlur={onCommit}
            size="md"
            isDisabled={isDisabled}
            isInvalid={Boolean(errors.lastName)}
            hint={errors.lastName}
          />
        </div>
        <Input
          label="Preferred name"
          value={state.preferredName}
          onChange={(v) => onChange({ preferredName: v })}
          onBlur={onCommit}
          size="md"
          isDisabled={isDisabled}
          hint="We'll address them by this name in the app and emails"
        />

        <div className="flex flex-col gap-2">
          <Select
            label="Relationship to you"
            placeholder="Select a relationship"
            size="md"
            items={RELATIONSHIP_OPTIONS}
            selectedKey={state.relationship}
            onSelectionChange={(key) => {
              onChange({ relationship: key as CaregiverRelationshipKind });
              onCommit();
            }}
            isDisabled={isDisabled}
          >
            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
          <div className="flex gap-6">
            <Checkbox
              isSelected={state.isStep}
              onChange={(v) => {
                onChange({ isStep: v });
                onCommit();
              }}
              size="md"
              label="Step"
            />
            <Checkbox
              isSelected={state.isInLaw}
              onChange={(v) => {
                onChange({ isInLaw: v });
                onCommit();
              }}
              size="md"
              label="In law"
            />
          </div>
        </div>

        <DateOfBirthInput
          value={state.dateOfBirth}
          onChange={(v) => {
            onChange({ dateOfBirth: v });
            onCommit();
          }}
          isInvalid={Boolean(errors.dateOfBirth)}
          hint={errors.dateOfBirth}
        />
      </CardSection>

      <div className="border-secondary border-t" />

      <CardSection label="Contact information">
        <div className="flex flex-col gap-2">
          <Input
            label="Email address"
            icon={MailIcon}
            type="email"
            placeholder="name@email.com"
            value={state.email}
            onChange={(v) => onChange({ email: v })}
            onBlur={onCommit}
            size="md"
            isRequired={isAccountBacked}
            isDisabled={isDisabled}
            isInvalid={Boolean(errors.email)}
            hint={errors.email}
          />
          {/* Before anyone enrolls there is no confirmation to lose, so there is no cost to state. */}
          {isAccountBacked && isEmailChanged && !errors.email && (
            <span className="text-warning-primary text-xs font-medium">{EMAIL_RECONFIRM_NOTE}</span>
          )}
        </div>
        <VerifiablePhoneField
          label="Phone"
          placeholder="(555) 000-0000"
          value={state.phone}
          onChange={(v) => onChange({ phone: v })}
          onBlur={onCommit}
          hint="For account notifications."
          // Required once they have enrolled: the account is reached on it, and
          // there is no write that takes it away.
          isRequired={isAccountBacked}
          isDisabled={isDisabled || needsEmailFirst}
          tooltip={needsEmailFirst ? EMAIL_FIRST_NOTE : undefined}
          // Server refusals arrive here — a number verified on another account,
          // or one it could not parse. Left undefined, the input falls back to
          // its own format validation.
          validation={errors.phone ? { isValid: false, errorMessage: errors.phone, e164Value: null } : undefined}
          verifiedAt={phoneNumberVerifiedAt}
          isChanged={isPhoneChanged}
          hasError={Boolean(errors.phone)}
          onVerify={onStartVerification}
          verifyTooltip={VERIFY_TOOLTIP}
          isVerificationRunning={Boolean(phoneVerificationStartedAt) || isStartingVerification}
          verifyBlockedReason={needsEmailFirst ? EMAIL_FIRST_NOTE : null}
        />
      </CardSection>

      <div className="border-secondary border-t" />

      <CardSection label="Living situation">
        {isPartnerCard && partnerName && (
          <Checkbox
            isSelected={liveTogether === true}
            onChange={onLiveTogetherChange}
            size="md"
            label={`Lives with ${partnerName}`}
            isDisabled={isDisabled}
          />
        )}

        {mode === 'referential' ? (
          <p className="text-tertiary text-sm">
            {name} lives with {partnerName}. Their residence details are on {partnerName}&rsquo;s card.
          </p>
        ) : (
          <>
            <Checkbox
              isSelected={state.residence.liveWithCaregiver === true}
              onChange={(v) => {
                updateResidence({ liveWithCaregiver: v });
                onCommit();
              }}
              size="md"
              label="Lives with you"
              isDisabled={isDisabled}
            />

            {state.residence.liveWithCaregiver === true ? (
              <CaregiverResidenceDisplay residence={caregiverResidence} />
            ) : (
              <>
                <AddressAutocomplete
                  apiKey={apiKey}
                  label="Address"
                  placeholder="123 Main St, San Francisco, CA"
                  value={state.residence.addressText}
                  includedPrimaryTypes={['street_address']}
                  isDisabled={isDisabled}
                  isInvalid={Boolean(errors.address)}
                  hint={errors.address}
                  onInputChange={(text) =>
                    updateResidence({
                      addressText: text,
                      address: state.residence.address?.formatted === text ? state.residence.address : null,
                    })
                  }
                  onSelect={(parsed) => {
                    updateResidence({ addressText: parsed.formattedAddress, address: toResidenceAddress(parsed) });
                    onCommit();
                  }}
                />
                <ResidenceTypeFields
                  state={state.residence}
                  onChange={updateResidence}
                  onCommit={onCommit}
                  errors={errors}
                  isDisabled={isDisabled}
                />
              </>
            )}
          </>
        )}
      </CardSection>

      {mode === 'main' && (
        <>
          <div className="border-secondary border-t" />

          <CardSection label="Frequent locations">
            <p className="text-secondary text-md">
              Is there anywhere away from home that {name} frequently spends time? We don&rsquo;t need exact locations,
              so you can enter cities or specific addresses.
            </p>
            <FrequentLocationsForm
              places={cardFrequentPlaces}
              onChange={handleFrequentPlacesChange}
              onCommit={onCommit}
              apiKey={apiKey}
              isDisabled={isDisabled}
              draftKey={dependentId}
            />
          </CardSection>
        </>
      )}
    </>
  );
};

DependentCardBody.displayName = 'DependentCardBody';

export default DependentCardBody;
